import { useMemo, useState } from 'react';

import BallotOutlinedIcon from '@mui/icons-material/BallotOutlined';
import { Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';

import Button from '@/components/Buttons/Button';
import { TABNAME_MAX_LENGTH } from '@/constants/name';
import { DataHubCard } from '@/features/DataHub/components/DataHubCard';
import { ExportDrawerCancelButton } from '@/features/DataHub/components/ExportDrawer/ExportDrawerCancelButton';
import { ExportDrawerLayout } from '@/features/DataHub/components/ExportDrawer/ExportDrawerLayout';
import { ExportNameCard } from '@/features/DataHub/components/ExportDrawer/ExportNameCard';
import ExportSettings from '@/features/DataHub/modules/Export/components/ExportSettings';
import {
  useExportRequest,
  useExportSettings
} from '@/features/DataHub/modules/Export/context/exportContexts';
import { validateExportNameWithMaxLength } from '@/features/DataHub/modules/Export/services/ExportNameService';
import { getDateRangeValidation, getFromToValues } from '@/features/DataHub/modules/Export/utils';
import type { OtherGroupIndexEntry } from '@/features/Markets/api/otherGroupIndex.api';
import { fetchOtherGroupIndex } from '@/features/Markets/api/otherGroupIndex.api';
import { useAppSelector } from '@/hooks/redux-hooks';
import type { RestrictedTableExportSymbol } from '@/slices/drawerSlice';
import { triggerBlobDownload } from '@/utils/triggerBlobDownload';

function buildCsv(
  symbols: RestrictedTableExportSymbol[],
  results: OtherGroupIndexEntry[][]
): string {
  const rows = ['Code,PeriodType,Period,TimeRef,Group,Date,PeriodAbs,Index,Mid'];
  symbols.forEach((sym, i) => {
    for (const entry of results[i] ?? []) {
      rows.push(
        [
          sym.key.Code ?? '',
          sym.key.PeriodType ?? '',
          sym.key.Period ?? '',
          sym.key.TimeRef ?? '',
          sym.group,
          entry.transactionTime,
          entry.values.PeriodAbs,
          entry.values.Index ?? '',
          entry.values.Mid ?? ''
        ].join(',')
      );
    }
  });
  return rows.join('\n');
}

export const RestrictedTableExportView = () => {
  const context = useAppSelector(state => state.drawer.restrictedTableExportContext);
  const [exportName, setExportName] = useState(context?.tabName ?? '');
  const { exportSettings } = useExportSettings();
  const { exportStatus, setExportStatus } = useExportRequest();
  const isDownloading = exportStatus === 'loading';

  const { dateRangeType, from, to, periodNumber } = exportSettings;

  const exportNameError = useMemo(
    () => validateExportNameWithMaxLength(exportName, TABNAME_MAX_LENGTH),
    [exportName]
  );

  const { periodNumberError, fromError, toError } = useMemo(
    () => getDateRangeValidation({ dateRangeType, from, to, periodNumber }),
    [dateRangeType, from, to, periodNumber]
  );
  const hasDateSettingsError = periodNumberError || fromError || toError;

  const isDisabled = isDownloading || Boolean(exportNameError) || hasDateSettingsError;

  const handleExport = async () => {
    if (!context) return;
    setExportStatus('loading');
    try {
      const { fromValue, toValue } = getFromToValues(exportSettings);
      const options = { from: fromValue, to: toValue || undefined };
      const results = await Promise.all(
        context.symbols.map(sym => fetchOtherGroupIndex(sym.key, sym.group, options))
      );
      const csv = buildCsv(context.symbols, results);
      const blob = new Blob([csv], { type: 'text/csv' });
      triggerBlobDownload(blob, `${exportName || context.tabName}.csv`);
    } catch {
      enqueueSnackbar('Export failed. Please try again.', { variant: 'error' });
    } finally {
      setExportStatus('idle');
    }
  };

  return (
    <ExportDrawerLayout
      footer={
        <>
          <ExportDrawerCancelButton />
          <Button
            disabled={isDisabled}
            variant='contained'
            onClick={handleExport}
          >
            Export
          </Button>
        </>
      }
    >
      <ExportNameCard
        exportName={exportName}
        exportNameError={exportNameError}
        onChange={setExportName}
      />
      <ExportSettings defaultExpanded />
      <DataHubCard
        expandable={false}
        icon={<BallotOutlinedIcon sx={{ fontSize: '16px' }} />}
        title={
          <>
            Format: <Typography sx={{ fontSize: 14 }}>CSV</Typography>
          </>
        }
      />
    </ExportDrawerLayout>
  );
};
